import React, { useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";

export default function CandleChart({
  data,
  fastEma, // [{ time, value }, ...] from backend
  slowEma, // [{ time, value }, ...] from backend
  slowerEma, // [{ time, value }, ...] from backend
  useAdaptiveEma, // still passed, but logic is in backend now
  timeframe,
  upperBand,
  lowerBand,
  emaMode,
  market,
}) {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);

  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);

  const ocr1SeriesRef = useRef(null);
  const ocr2SeriesRef = useRef(null);
  const ocr4SeriesRef = useRef(null);

  const ma1SeriesRef = useRef(null); // fast EMA
  const ma2SeriesRef = useRef(null); // slow EMA
  const ma3SeriesRef = useRef(null); // slower EMA

  const ema1UpperSeriesRef = useRef(null);
  const ema1LowerSeriesRef = useRef(null);

  // 1️⃣ Create chart ONCE
  useEffect(() => {
    if (!chartContainerRef.current) return;

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 500,
      layout: {
        background: { color: "#ffffff" },
        textColor: "#333333",
      },
      grid: {
        vertLines: { color: "#eeeeee" },
        horzLines: { color: "#eeeeee" },
      },
      timeScale: {
        borderColor: "#cccccc",
      },
      rightPriceScale: {
        borderColor: "#cccccc",
        priceScaleId: "right",
      },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#4caf50",
      downColor: "#f44336",
      borderUpColor: "#4caf50",
      borderDownColor: "#f44336",
      wickUpColor: "#4caf50",
      wickDownColor: "#f44336",
    });

    // EMA lines
    const ma1Series = chart.addLineSeries({
      color: "red",
      lineWidth: 2,
      priceScaleId: "right",
    });

    const ma2Series = chart.addLineSeries({
      color: "green",
      lineWidth: 2,
      priceScaleId: "right",
    });

    const ma3Series = chart.addLineSeries({
      color: "blue",
      lineWidth: 2,
      priceScaleId: "right",
    });

    const ema1UpperSeries = chart.addLineSeries({
      color: "orange",
      lineWidth: 2,
      priceScaleId: "right",
    });

    const ema1LowerSeries = chart.addLineSeries({
      color: "orange",
      lineWidth: 2,
      priceScaleId: "right",
    });

    ma1SeriesRef.current = ma1Series;
    ma2SeriesRef.current = ma2Series;
    ma3SeriesRef.current = ma3Series;

    ema1UpperSeriesRef.current = ema1UpperSeries;
    ema1LowerSeriesRef.current = ema1LowerSeries;

    chart.priceScale("right").applyOptions({
      autoScale: true,
      scaleMargins: {
        top: 0.05,
        bottom: 0.5,
      },
    });

    // OCR panels
    const ocr1Series = chart.addLineSeries({
      priceScaleId: "ocr1-scale",
      color: "red",
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
    });

    const ocr2Series = chart.addLineSeries({
      priceScaleId: "ocr2-scale",
      color: "blue",
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
    });

    const ocr4Series = chart.addLineSeries({
      priceScaleId: "ocr4-scale",
      color: "green",
      lineWidth: 2,
      lastValueVisible: false,
      priceLineVisible: false,
    });

    chart.priceScale("ocr1-scale").applyOptions({
      visible: true,
      autoScale: true,
      scaleMargins: {
        top: 0.55,
        bottom: 0.2,
      },
    });

    chart.priceScale("ocr2-scale").applyOptions({
      visible: true,
      autoScale: true,
      scaleMargins: {
        top: 0.55,
        bottom: 0.2,
      },
    });

    chart.priceScale("ocr4-scale").applyOptions({
      visible: true,
      autoScale: true,
      scaleMargins: {
        top: 0.55,
        bottom: 0.2,
      },
    });

    const volumeSeries = chart.addHistogramSeries({
      priceScaleId: "volume",
      priceFormat: { type: "volume" },
    });

    chart.priceScale("volume").applyOptions({
      scaleMargins: {
        top: 0.75,
        bottom: 0,
      },
    });

    chart.applyOptions({
      timeScale: {
        rightOffset: 3,
        barSpacing: 6,
        fixLeftEdge: false,
        fixRightEdge: false,
        rightBarStaysOnScroll: true,
        lockVisibleTimeRangeOnResize: false,
        shiftVisibleRangeOnNewBar: true,
      },
      rightPriceScale: {
        scaleMargins: {
          top: 0.1,
          bottom: 0.3,
        },
      },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;
    ocr1SeriesRef.current = ocr1Series;
    ocr2SeriesRef.current = ocr2Series;
    ocr4SeriesRef.current = ocr4Series;

    const handleResize = () => {
      if (!chartContainerRef.current) return;
      chart.applyOptions({
        width: chartContainerRef.current.clientWidth,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.remove();
    };
  }, []);

  // 2️⃣ Update chart data whenever data or EMA/bands change
  useEffect(() => {
    if (
      !candleSeriesRef.current ||
      !volumeSeriesRef.current ||
      !ma1SeriesRef.current ||
      !ma2SeriesRef.current ||
      !ma3SeriesRef.current
    ) {
      return;
    }

    const safeData = Array.isArray(data) ? data : [];

    const candleData = safeData.map((d) => ({
      time: d.datetime,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
    }));

    const volumeData = safeData.map((d) => ({
      time: d.datetime,
      value: d.volume || 0,
      color: d.close >= d.open ? "#4caf50" : "#f44336",
    }));

    const ocr1Data = safeData.map((d) => ({
      time: d.datetime,
      value: d.ocr1 ?? 0,
    }));

    const ocr2Data = safeData.map((d) => ({
      time: d.datetime,
      value: d.ocr2 ?? 0,
    }));

    const ocr4Data = safeData.map((d) => ({
      time: d.datetime,
      value: d.ocr4 ?? 0,
    }));

    // EMA data comes directly from backend via props
    const ma1Data = Array.isArray(fastEma) ? fastEma : [];
    const ma2Data = Array.isArray(slowEma) ? slowEma : [];
    const ma3Data = Array.isArray(slowerEma) ? slowerEma : [];

    // Upper / lower bands based on fast EMA
    const ema1UpperData = ma1Data.map((p) => ({
      time: p.time,
      value: p.value * upperBand,
    }));

    const ema1LowerData = ma1Data.map((p) => ({
      time: p.time,
      value: p.value * lowerBand,
    }));

    // Clear then set (to avoid ghost artifacts)
    candleSeriesRef.current.setData([]);
    volumeSeriesRef.current.setData([]);

    ma1SeriesRef.current.setData([]);
    ma2SeriesRef.current.setData([]);
    ma3SeriesRef.current.setData([]);

    ema1UpperSeriesRef.current.setData([]);
    ema1LowerSeriesRef.current.setData([]);

    ocr1SeriesRef.current.setData([]);
    ocr2SeriesRef.current.setData([]);
    ocr4SeriesRef.current.setData([]);

    candleSeriesRef.current.setData(candleData);
    volumeSeriesRef.current.setData(volumeData);

    ma1SeriesRef.current.setData(ma1Data);
    ma2SeriesRef.current.setData(ma2Data);
    ma3SeriesRef.current.setData(ma3Data);

    ema1UpperSeriesRef.current.setData(ema1UpperData);
    ema1LowerSeriesRef.current.setData(ema1LowerData);

    ocr1SeriesRef.current.setData(ocr1Data);
    ocr2SeriesRef.current.setData(ocr2Data);
    ocr4SeriesRef.current.setData(ocr4Data);

    // Anchor the view to the most recent candle right after data loads —
    // without this, nothing explicitly positions the initial view, and
    // the chart can leave recent candles off-screen to the right.
    if (chartRef.current) {
      chartRef.current.timeScale().scrollToRealTime();
    }
  }, [data, fastEma, slowEma, slowerEma, upperBand, lowerBand]);

  // 3️⃣ Keep chart responsive
  useEffect(() => {
    if (!chartRef.current || !chartContainerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (!chartContainerRef.current || !chartRef.current) return;

      chartRef.current.applyOptions({
        width: chartContainerRef.current.clientWidth,
      });

      chartRef.current.timeScale().fitContent();
    });

    resizeObserver.observe(chartContainerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div ref={chartContainerRef} style={{ width: "100%", height: "500px" }} />
  );
}
